import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getLead, updateLead, deleteLead, convertLeadToStudent, convertLeadToProspect, initiateCall, scheduleCall, getVoicenterCalls, getCourses, createRegistration } from '../services/api';
import TasksTab from '../components/TasksTab';

export default function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { userName } = useUser();
  const [lead, setLead] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<any>({});
  const [successMsg, setSuccessMsg] = useState('');
  const [callHistory, setCallHistory] = useState<any[]>([]);
  const [showScheduleCallModal, setShowScheduleCallModal] = useState(false);
  const [scheduleCallForm, setScheduleCallForm] = useState({ scheduledDate: '', scheduledTime: '' });
  const [showRegFormModal, setShowRegFormModal] = useState(false);
  const [regForm, setRegForm] = useState({ courseName: '', tuitionAmount: '', academicYear: '', personalMessage: '', sendMethod: 'both' });
  const [coursesList, setCoursesList] = useState<any[]>([]);

  useEffect(() => {
    if (!id) return;
    getLead(id).then(res => setLead(res.data)).catch(() => {});
    getVoicenterCalls({ entityType: 'lead', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
    getCourses().then(res => setCoursesList(res.data)).catch(() => {});
  }, [id]);

  if (!lead) return <div className="page-section"><p>{t('loading')}</p></div>;

  const getName = () => {
    if (lang === 'ar' && lead.firstNameAr) return `${lead.firstNameAr} ${lead.lastNameAr}`;
    return `${lead.firstName} ${lead.lastName}`;
  };

  const getStageText = (stage: string) => t(`stages.${stage}`);
  const getSourceText = (source: string) => t(`sources.${source}`);

  const openWhatsApp = () => {
    const phone = lead.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/972${phone.startsWith('0') ? phone.slice(1) : phone}`, '_blank');
  };

  const openEmail = () => {
    window.open(`mailto:${lead.email}`, '_blank');
  };

  const handleConvert = async () => {
    if (window.confirm('להמיר ליד זה לסטודנט?')) {
      await convertLeadToStudent(id!);
      navigate('/students');
    }
  };

  const handleConvertToProspect = async () => {
    if (window.confirm(t('confirmConvertLeadToProspect'))) {
      await convertLeadToProspect(id!);
      navigate('/prospects');
    }
  };

  const handleInitiateCall = async () => {
    if (!id || !lead.phone) return;
    await initiateCall({ phoneNumber: lead.phone, entityType: 'lead', entityId: id, entityName: getName(), calledBy: userName });
    alert(lang === 'ar' ? 'بدأت المكالمة!' : 'השיחה התחילה!');
    getVoicenterCalls({ entityType: 'lead', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
  };

  const handleScheduleCall = async () => {
    if (!id || !lead.phone) return;
    await scheduleCall({ phoneNumber: lead.phone, entityType: 'lead', entityId: id, entityName: getName(), calledBy: userName, ...scheduleCallForm });
    setShowScheduleCallModal(false);
    setScheduleCallForm({ scheduledDate: '', scheduledTime: '' });
    alert(lang === 'ar' ? 'تم جدولة المكالمة!' : 'השיחה תוזמנה!');
    getVoicenterCalls({ entityType: 'lead', entityId: id }).then(res => setCallHistory(res.data)).catch(() => {});
  };

  const handleEditLead = () => {
    setEditForm({
      firstName: lead.firstName || '',
      lastName: lead.lastName || '',
      firstNameAr: lead.firstNameAr || '',
      lastNameAr: lead.lastNameAr || '',
      phone: lead.phone || '',
      email: lead.email || '',
      interest: lead.interest || '',
      source: lead.source || '',
      notes: lead.notes || '',
      followUpDate: lead.followUpDate || '',
      stage: lead.stage || '',
    });
    setIsEditing(true);
    setSuccessMsg('');
  };

  const handleSaveLead = async () => {
    if (!id) return;
    try {
      await updateLead(id, editForm);
      const res = await getLead(id);
      setLead(res.data);
      setIsEditing(false);
      setSuccessMsg('הנתונים נשמרו בהצלחה!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      console.error('Save failed', err);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleDeleteLead = async () => {
    if (!id) return;
    if (window.confirm('האם אתה בטוח שברצונך למחוק?')) {
      try {
        await deleteLead(id);
        navigate('/leads');
      } catch (err) {
        console.error('Delete failed', err);
      }
    }
  };

  const stageColors: Record<string, string> = {
    interested: 'badge-blue',
    contacted: 'badge-gold',
    meeting_scheduled: 'badge-yellow',
    meeting_attended: 'badge-green',
    applied: 'badge-blue',
    enrolled: 'badge-green',
    active_student: 'badge-green',
  };

  const formatPhoneForWhatsApp = (phone: string) => {
    const cleaned = phone.replace(/[^0-9]/g, '');
    return cleaned.startsWith('0') ? '972' + cleaned.slice(1) : cleaned;
  };

  const handleSendRegForm = async () => {
    if (!id || !regForm.courseName) return;
    try {
      const res = await createRegistration({
        entityType: 'lead',
        entityId: id,
        recipientName: `${lead.firstName} ${lead.lastName}`,
        recipientEmail: lead.email,
        recipientPhone: lead.phone,
        courseName: regForm.courseName,
        tuitionAmount: Number(regForm.tuitionAmount) || 0,
        academicYear: regForm.academicYear,
        personalMessage: regForm.personalMessage,
        sendMethod: regForm.sendMethod,
        prefillData: {
          firstName: lead.firstName || '',
          lastName: lead.lastName || '',
          nationalId: '',
          dateOfBirth: '',
          gender: '',
          phone: lead.phone || '',
          email: lead.email || '',
          city: '',
          zipCode: '',
        },
      });
      const regUrl = `${window.location.origin}/register/${res.data.id}`;
      if (regForm.sendMethod === 'whatsapp' || regForm.sendMethod === 'both') {
        const whatsappPhone = formatPhoneForWhatsApp(lead.phone);
        const msg = encodeURIComponent(`${regForm.personalMessage ? regForm.personalMessage + '\n' : ''}טופס רישום למכללת מנארה:\n${regUrl}`);
        window.open(`https://wa.me/${whatsappPhone}?text=${msg}`, '_blank');
      }
      if (regForm.sendMethod === 'email' || regForm.sendMethod === 'both') {
        const subject = encodeURIComponent('טופס רישום - מכללת מנארה');
        const body = encodeURIComponent(`${regForm.personalMessage ? regForm.personalMessage + '\n\n' : ''}טופס רישום:\n${regUrl}`);
        window.open(`mailto:${lead.email}?subject=${subject}&body=${body}`, '_blank');
      }
      setShowRegFormModal(false);
      setRegForm({ courseName: '', tuitionAmount: '', academicYear: '', personalMessage: '', sendMethod: 'both' });
      alert(lang === 'ar' ? 'تم إرسال نموذج التسجيل بنجاح!' : 'טופס הרישום נשלח בהצלחה!');
    } catch (err) {
      console.error('Send registration form failed', err);
    }
  };

  return (
    <div>
      <div className="page-section">
        <div className="detail-header">
          <div className="detail-avatar">{lead.firstName[0]}</div>
          <div className="detail-info">
            <h2>{getName()}</h2>
            <p dir="ltr">{lead.email} | {lead.phone}</p>
            <span className={`badge ${stageColors[lead.stage]}`}>{getStageText(lead.stage)}</span>
          </div>
          <div className="detail-actions">
            {lead.phone && <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }} onClick={handleInitiateCall}>📞 {t('callNow')}</button>}
            {lead.phone && <button className="btn btn-sm btn-whatsapp" onClick={openWhatsApp}>📱 {t('sendWhatsApp')}</button>}
            {lead.email && <button className="btn btn-sm btn-email" onClick={openEmail}>📧 {t('sendEmail')}</button>}
            <button className="btn btn-sm" style={{ background: '#E91E63', color: '#fff', border: 'none' }} onClick={() => setShowRegFormModal(true)}>📝 {lang === 'ar' ? 'إرسال نموذج تسجيل' : 'שלח טופס רישום'}</button>
            {lead.stage !== 'active_student' && (
              <>
                <button className="btn btn-sm btn-secondary" onClick={handleConvertToProspect}>📋 {t('convertToProspect')}</button>
                <button className="btn btn-sm btn-primary" onClick={handleConvert}>🎓 {t('convertToStudent')}</button>
              </>
            )}
            <Link to="/leads" className="btn btn-sm btn-outline">← {t('back')}</Link>
          </div>
        </div>

        {/* Edit/Save/Cancel/Delete Buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {!isEditing && (
            <button onClick={handleEditLead} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#2196F3' }}>
              עריכה
            </button>
          )}
          {isEditing && (
            <button onClick={handleSaveLead} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#4CAF50' }}>
              שמירה
            </button>
          )}
          {isEditing && (
            <button onClick={handleCancelEdit} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#9E9E9E' }}>
              ביטול
            </button>
          )}
          <button onClick={handleDeleteLead} style={{ padding: '8px 20px', borderRadius: 6, border: 'none', color: '#fff', fontWeight: 600, cursor: 'pointer', background: '#f44336' }}>
            מחיקה
          </button>
        </div>
        {successMsg && (
          <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '10px 16px', borderRadius: 6, marginBottom: 12, fontWeight: 500 }}>
            {successMsg}
          </div>
        )}

        <div className="tabs">
          <button className={`tab ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>{t('details')}</button>
          <button className={`tab ${activeTab === 'calls' ? 'active' : ''}`} onClick={() => setActiveTab('calls')}>📞 {t('calls')}</button>
          <button className={`tab ${activeTab === 'tasks' ? 'active' : ''}`} onClick={() => setActiveTab('tasks')}>📋 משימות</button>
        </div>

        {/* Lead Details - View Mode */}
        {activeTab === 'info' && !isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('firstName')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{lead.firstName}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{lead.lastName}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('firstName')} (ערבית)</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{lead.firstNameAr || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')} (ערבית)</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{lead.lastNameAr || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', direction: 'ltr', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 8 }}>
                {lead.phone}
                {lead.phone && <button onClick={handleInitiateCall} style={{ background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: 12 }}>📞 {t('callNow')}</button>}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', direction: 'ltr', textAlign: 'left' }}>{lead.email || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('interest')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{lead.interest || '-'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('source')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{getSourceText(lead.source)}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('stage')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{getStageText(lead.stage)}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{t('followUpDate')}</label>
              <div className="form-input" style={{ background: '#f8f9fa' }}>{lead.followUpDate || '-'}</div>
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('notes')}</label>
              <div className="form-input" style={{ background: '#f8f9fa', minHeight: 60 }}>{lead.notes || '-'}</div>
            </div>
          </div>
        )}

        {/* Lead Details - Edit Mode */}
        {activeTab === 'info' && isEditing && (
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{t('firstName')}</label>
              <input className="form-input" value={editForm.firstName} onChange={e => setEditForm({...editForm, firstName: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')}</label>
              <input className="form-input" value={editForm.lastName} onChange={e => setEditForm({...editForm, lastName: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('firstName')} (ערבית)</label>
              <input className="form-input" value={editForm.firstNameAr} onChange={e => setEditForm({...editForm, firstNameAr: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('lastName')} (ערבית)</label>
              <input className="form-input" value={editForm.lastNameAr} onChange={e => setEditForm({...editForm, lastNameAr: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('phone')}</label>
              <input className="form-input" type="tel" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('email')}</label>
              <input className="form-input" type="email" value={editForm.email} onChange={e => setEditForm({...editForm, email: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('interest')}</label>
              <input className="form-input" value={editForm.interest} onChange={e => setEditForm({...editForm, interest: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('source')}</label>
              <select className="form-select" value={editForm.source} onChange={e => setEditForm({...editForm, source: e.target.value})}>
                <option value="facebook">{t('sources.facebook')}</option>
                <option value="website">{t('sources.website')}</option>
                <option value="referral">{t('sources.referral')}</option>
                <option value="fair">{t('sources.fair')}</option>
                <option value="whatsapp">{t('sources.whatsapp')}</option>
                <option value="other">{t('sources.other')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('stage')}</label>
              <select className="form-select" value={editForm.stage} onChange={e => setEditForm({...editForm, stage: e.target.value})}>
                <option value="interested">{t('stages.interested')}</option>
                <option value="contacted">{t('stages.contacted')}</option>
                <option value="meeting_scheduled">{t('stages.meeting_scheduled')}</option>
                <option value="meeting_attended">{t('stages.meeting_attended')}</option>
                <option value="applied">{t('stages.applied')}</option>
                <option value="enrolled">{t('stages.enrolled')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('followUpDate')}</label>
              <input className="form-input" type="date" value={editForm.followUpDate} onChange={e => setEditForm({...editForm, followUpDate: e.target.value})} />
            </div>
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="form-label">{t('notes')}</label>
              <textarea className="form-textarea" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} style={{ minHeight: 60 }} />
            </div>
          </div>
        )}

        {/* Calls Tab */}
        {activeTab === 'calls' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn btn-sm" style={{ background: '#4CAF50', color: '#fff', border: 'none' }} onClick={handleInitiateCall}>📞 {t('callNow')}</button>
              <button className="btn btn-sm" style={{ background: '#FF9800', color: '#fff', border: 'none' }} onClick={() => setShowScheduleCallModal(true)}>🕐 {t('scheduleCallBtn')}</button>
            </div>
            <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>{t('callTime')}</th>
                  <th>{t('callDuration')}</th>
                  <th>{t('calledBy')}</th>
                  <th>{t('callDirection')}</th>
                  <th>{t('callStatus')}</th>
                  <th>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {callHistory.map((c: any) => (
                  <tr key={c.id}>
                    <td>{c.date}</td>
                    <td dir="ltr">{c.time}</td>
                    <td>{c.duration ? `${c.duration} ${t('sec')}` : '-'}</td>
                    <td>{c.calledBy || '-'}</td>
                    <td>
                      <span className={`badge ${c.direction === 'incoming' ? 'badge-blue' : 'badge-green'}`}>
                        {c.direction === 'incoming' ? `📥 ${t('incoming')}` : `📤 ${t('outgoing')}`}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${c.status === 'answered' ? 'badge-green' : c.status === 'no_answer' ? 'badge-red' : c.status === 'busy' ? 'badge-yellow' : c.status === 'scheduled' ? 'badge-blue' : 'badge-gray'}`}>
                        {t(c.status) || c.status}
                      </span>
                    </td>
                    <td>
                      {c.recordingUrl && (
                        <button className="btn btn-sm btn-outline" onClick={() => window.open(c.recordingUrl, '_blank')}>🎧 {t('listenRecording')}</button>
                      )}
                    </td>
                  </tr>
                ))}
                {callHistory.length === 0 && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {/* Schedule Call Modal */}
        {showScheduleCallModal && (
          <div className="modal-overlay" onClick={() => setShowScheduleCallModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">🕐 {t('scheduleCallTitle')}</h3>
                <button className="modal-close" onClick={() => setShowScheduleCallModal(false)}>×</button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('scheduledDate')}</label>
                  <input className="form-input" type="date" value={scheduleCallForm.scheduledDate} onChange={e => setScheduleCallForm({...scheduleCallForm, scheduledDate: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('scheduledTime')}</label>
                  <input className="form-input" type="time" value={scheduleCallForm.scheduledTime} onChange={e => setScheduleCallForm({...scheduleCallForm, scheduledTime: e.target.value})} />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleScheduleCall} disabled={!scheduleCallForm.scheduledDate || !scheduleCallForm.scheduledTime}>🕐 {t('scheduleCallBtn')}</button>
                <button className="btn btn-outline" onClick={() => setShowScheduleCallModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'tasks' && id && (
          <TasksTab entityType="lead" entityId={id} />
        )}

        {/* Send Registration Form Modal */}
        {showRegFormModal && (
          <div className="modal-overlay" onClick={() => setShowRegFormModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
              <div className="modal-header">
                <h3 className="modal-title">📝 {lang === 'ar' ? 'إرسال نموذج تسجيل' : 'שלח טופס רישום'}</h3>
                <button className="modal-close" onClick={() => setShowRegFormModal(false)}>&times;</button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'اسم المستلم' : 'שם הנמען'}</label>
                  <input className="form-input" value={`${lead.firstName} ${lead.lastName}`} readOnly style={{ background: '#f0f0f0' }} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('email')}</label>
                  <input className="form-input" value={lead.email || ''} readOnly style={{ background: '#f0f0f0' }} dir="ltr" />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('phone')}</label>
                  <input className="form-input" value={lead.phone || ''} readOnly style={{ background: '#f0f0f0' }} dir="ltr" />
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'المساق/البرنامج' : 'מסלול/קורס'} *</label>
                  <select className="form-select" value={regForm.courseName} onChange={e => setRegForm({...regForm, courseName: e.target.value})}>
                    <option value="">{lang === 'ar' ? 'اختر...' : 'בחר...'}</option>
                    {coursesList.map((c: any) => (
                      <option key={c.id} value={lang === 'ar' ? c.nameAr || c.name : c.name}>{lang === 'ar' ? c.nameAr || c.name : c.name}</option>
                    ))}
                    {lead.interest && <option value={lead.interest}>{lead.interest} ({lang === 'ar' ? 'مجال الاهتمام' : 'תחום עניין'})</option>}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'الرسوم الدراسية' : 'שכר לימוד'}</label>
                  <input className="form-input" type="number" value={regForm.tuitionAmount}
                    onChange={e => setRegForm({...regForm, tuitionAmount: e.target.value})} placeholder="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">{lang === 'ar' ? 'السنة الدراسية' : 'שנת לימודים'}</label>
                  <input className="form-input" value={regForm.academicYear}
                    onChange={e => setRegForm({...regForm, academicYear: e.target.value})} placeholder="2026" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">{lang === 'ar' ? 'رسالة شخصية' : 'הודעה אישית'} ({lang === 'ar' ? 'اختياري' : 'אופציונלי'})</label>
                  <textarea className="form-textarea" value={regForm.personalMessage}
                    onChange={e => setRegForm({...regForm, personalMessage: e.target.value})} style={{ minHeight: 60 }} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">{lang === 'ar' ? 'طريقة الإرسال' : 'אמצעי שליחה'}</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[{ key: 'email', label: lang === 'ar' ? 'بريد إلكتروني' : 'דואל' },
                      { key: 'whatsapp', label: lang === 'ar' ? 'واتساب' : 'ווטסאפ' },
                      { key: 'both', label: lang === 'ar' ? 'كلاهما' : 'שניהם' }
                    ].map(opt => (
                      <label key={opt.key} style={{
                        padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                        border: `2px solid ${regForm.sendMethod === opt.key ? '#7B1FA2' : '#ddd'}`,
                        background: regForm.sendMethod === opt.key ? '#f3e5f5' : '#fff',
                      }}>
                        <input type="radio" name="sendMethodLead" value={opt.key} checked={regForm.sendMethod === opt.key}
                          onChange={e => setRegForm({...regForm, sendMethod: e.target.value})} style={{ display: 'none' }} />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-primary" onClick={handleSendRegForm} disabled={!regForm.courseName}>📝 {lang === 'ar' ? 'إرسال النموذج' : 'שלח טופס'}</button>
                <button className="btn btn-outline" onClick={() => setShowRegFormModal(false)}>{t('cancel')}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
